import React, { useState, FormEvent, useEffect } from 'react';
import { FormGroup, InputGroup, Button, AnchorButton, Checkbox, HTMLTable, Spinner } from "@blueprintjs/core";

import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import * as utils from "./utils";
import { Environment, GlData, GlDataItem, GlDataIncomeItem, BalanceSheetTotals, BalanceSheetTotalsItem } from './Types';

interface BalanceSheetProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function BalanceSheet({ svc, currentEnv = {} as Environment }: BalanceSheetProps) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState({} as GlData)
    const [totals, setTotals] = useState([0,0,0,0])
    const [bsTotals, setBSTotals] = useState({} as BalanceSheetTotals)
    const [hasGeneral, setHasGeneral] = useState(false)

    //let date = new Date(), y = date.getFullYear(), m = date.getMonth()
    //const [from, setFrom] = useState(utils.formatDate(new Date(y, m, 1)))
    //const [to, setTo] = useState(utils.formatDate(date)) // new Date(y, m+1, 0)
    const [from, setFrom] = useState(utils.formatDate(new Date(currentEnv.startOfYear)))
    const [to, setTo] = useState(utils.formatDateMin(new Date(), new Date(currentEnv.endOfYear)))
    const [unposted, setUnposted] = useState(true)
    const [general, setGeneral] = useState(false)
    const [zero, setZero] = useState(false)

    useEffect(() => {
        setFrom(utils.formatDate(new Date(currentEnv.startOfYear)))
        setTo(utils.formatDateMin(new Date(), new Date(currentEnv.endOfYear)))
    }, [currentEnv])
    /*
    useEffect(() => {
        (async () => {
            try {
                const res = await svc.post('/getdates',
                    {});
                setFrom(res.data.data.startOfYear)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }, [svc])
    */

    useEffect(() => {
        //console.log(totals)
    }, [totals])

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/gldata',
                {
                    type: "BS",
                    //from: from,
                    to: to,
                    includesUnposted: unposted,
                    includesGeneral: general,
                    includesZero: zero,
                });

            //console.log(res.data.data.data)
            setIsLoading(false)
            setData(res.data.data)
            setHasGeneral(res.data.data.includesGeneral)

            let pnl = 0, totalA = 0, totalL = 0, totalC = 0, ccyTotals = {} as BalanceSheetTotals
            pnl = res.data.data.netIncome
            Object.entries(res.data.data.incomes).map((income, i) => {
                if (!(income[0] in ccyTotals)) ccyTotals[income[0]] = {pnl: 0, totalA: 0, totalL: 0, totalC: 0} as BalanceSheetTotalsItem
                ccyTotals[income[0]].pnl = (income[1] as GlDataIncomeItem).fincome
            })
            for (let i = 0; i < res.data.data.data.length; i++) {
                let bal = res.data.data.data[i] as GlDataItem
                if (bal.acType === "A" && !bal.isGeneral) {
                    totalA += utils.roundMoney(bal.cf)
                }
                if (bal.acType === "L" && !bal.isGeneral) {
                    totalL += utils.roundMoney(bal.cf)
                }
                if (bal.acType === "C" && !bal.isGeneral) {
                    totalC += utils.roundMoney(bal.cf)
                }
                Object.entries(bal.fbal).map((fbal, i) => {
                    if (!(fbal[0] in ccyTotals)) ccyTotals[fbal[0]] = {pnl: 0, totalA: 0, totalL: 0, totalC: 0} as BalanceSheetTotalsItem
                    if (bal.acType === "A" && !bal.isGeneral) {
                        ccyTotals[fbal[0]].totalA += utils.roundMoney(fbal[1].fcf)
                    }
                    if (bal.acType === "L" && !bal.isGeneral) {
                        ccyTotals[fbal[0]].totalL += utils.roundMoney(fbal[1].fcf)
                    }
                    if (bal.acType === "C" && !bal.isGeneral) {
                        ccyTotals[fbal[0]].totalC += utils.roundMoney(fbal[1].fcf)
                    }
                })
            }
            setTotals([pnl, totalA, totalL, totalC])
            setBSTotals(ccyTotals)
            // console.log("totalC = ", totalC)
        } catch (error) {
            utils.showError(error)
        }
    }

    let pageMaxWidth = currentEnv.multiCurrency ? "1100px" : "700px"
    let pageTitle = t('balance_sheet')
    let list = <div className="Print-Align-C" style={{maxWidth: pageMaxWidth}}>
        {isLoading && <Spinner></Spinner>}
        <div className="Spacing-V-16" />
        <div className="No-Screen">
            <div className="Row" style={{maxWidth: pageMaxWidth}}>
                <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{currentEnv.companyName}</div>
            </div>
            <div className="Spacing-V-8" />
            {currentEnv.companyAddress && currentEnv.companyAddress.split('\n').map((line) =>
                <div className="Row" style={{maxWidth: pageMaxWidth}}>
                    <div className="Col Align-C">{line}</div>
                </div>
            )}
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{pageTitle}</div>
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            {/*div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('from')}: {data.from}<div className="Spacing-H-8"/>{t('to')}: {data.to}</div>*/}
            <div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('as_of')}: {data.to}</div>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                {currentEnv.multiCurrency && <tr>
                    <th></th>
                    <th></th>
                    <th colSpan={2}><div className="Align-C">{t('in_base_currency')}</div></th>
                    <th className="THead-Sep"></th>
                    <th colSpan={2}><div className="Align-C">{t('in_foreign_currency')}</div></th>
                    <th></th>
                </tr>}
                <tr>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>
                    {currentEnv.multiCurrency && <>
                    <th className="THead-Sep"></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>
                    <th className="TCol-Currency">{t('currency')}</th>
                    </>}
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((item: GlDataItem) => item.acType === "A" &&
                    <>
                    {/* <tr key={item.acCode}>
                        <td className="TCol-ACCode">{item.acCode}</td>
                        <td className="TCol-ACName">{hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral ? utils.formatMoney(item.cf) : ''}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral ? '' : utils.formatMoney(item.cf)}</div></td>
                        {currentEnv.multiCurrency && <td className="TCol-Currency"></td>}
                    </tr> */}
                    {utils.isEmpty(item.fbal) && <tr>
                        <td className="TCol-ACCode">{item.acCode}</td>
                        <td className="TCol-ACName">{(hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-Currency"></td>
                        </>}
                    </tr>}
                    {Object.entries(item.fbal).map((bal, i) => <tr key={item.acCode+"-"+bal[0]}>
                        <td className="TCol-ACCode">{i === 0 && item.acCode}</td>
                        <td className="TCol-ACName">{i === 0 && (hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(bal[1].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(bal[1].cf)}</div></td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(bal[1].fcf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(bal[1].fcf)}</div></td>
                        <td className="TCol-Currency">{bal[0] === "" ? currentEnv.baseCurrency : bal[0]}</td>
                        </>}
                    </tr>)}
                    </>
                )}
            {/* </tbody>
            <tfoot> */}
                {/* <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"><div className="Indent-2"><b>{t('total_assets')}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(totals[1])}</b></div></td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-Currency"></td>
                    </>}
                </tr> */}
                {Object.entries(bsTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].totalA != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('total_assets')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totals[1])}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(ccyTotals[1].totalA)}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}
            {/* </tfoot> */}
            </tbody>
        </HTMLTable>
        <HTMLTable striped interactive condensed>
            <thead>
                {currentEnv.multiCurrency && <tr>
                    <th></th>
                    <th></th>
                    <th colSpan={2}><div className="Align-C">{t('in_base_currency')}</div></th>
                    <th className="THead-Sep"></th>
                    <th colSpan={2}><div className="Align-C">{t('in_foreign_currency')}</div></th>
                    <th></th>
                </tr>}
                <tr>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>
                    {currentEnv.multiCurrency && <>
                    <th className="THead-Sep"></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>
                    <th className="TCol-Currency">{t('currency')}</th>
                    </>}
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((item: GlDataItem) => item.acType === "L" &&
                    <>
                    {/* <tr key={item.acCode}>
                        <td className="TCol-ACCode">{item.acCode}</td>
                        <td className="TCol-ACName">{hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral ? utils.formatMoney(-item.cf) : ''}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral ? '' : utils.formatMoney(-item.cf)}</div></td>
                    </tr> */}
                    {Object.entries(item.fbal).map((bal, i) => <tr>
                        <td className="TCol-ACCode">{i === 0 && item.acCode}</td>
                        <td className="TCol-ACName">{i === 0 && (hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].cf)}</div></td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].fcf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].fcf)}</div></td>
                        <td className="TCol-Currency">{bal[0] === "" ? currentEnv.baseCurrency : bal[0]}</td>
                        </>}
                    </tr>
                    )}
                    </>
                )}
                {/* <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"><div className="Indent-2"><b>{t('total_liabilities')}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-totals[2])}</b></div></td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-Currency"></td>
                    </>}
                </tr> */}
                {Object.entries(bsTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].totalL != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('total_liabilities')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-totals[2])}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-ccyTotals[1].totalL)}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}

                {data.data && data.data.map((item: GlDataItem) => item.acType === "C" &&
                    <>
                    {/* <tr key={item.acCode}>
                        <td className="TCol-ACCode">{item.acCode}</td>
                        <td className="TCol-ACName">{hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral ? utils.formatMoney(-item.cf) : ''}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral ? '' : utils.formatMoney(-item.cf)}</div></td>
                    </tr> */}
                    {Object.entries(item.fbal).map((bal, i) => <tr>
                        <td className="TCol-ACCode">{i === 0 && item.acCode}</td>
                        <td className="TCol-ACName">{i === 0 && (hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].cf)}</div></td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].fcf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].fcf)}</div></td>
                        <td className="TCol-Currency">{bal[0] === "" ? currentEnv.baseCurrency : bal[0]}</td>
                        </>}
                    </tr>
                    )}
                    </>
                )}
                {/* <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"><div className="Indent-2"><b>{t('pl_current_year')}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-totals[0])}</b></div></td>
                </tr> */}
                {Object.entries(bsTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].pnl != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('pl_current_year')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-totals[0])}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-ccyTotals[1].pnl)}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}

                {/* <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"><div className="Indent-2"><b>{t('total_equity')}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-(totals[0]+totals[3]))}</b></div></td>
                </tr> */}
                {Object.entries(bsTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].pnl+ccyTotals[1].totalC != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('total_equity')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-(totals[0]+totals[3]))}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-(ccyTotals[1].pnl+ccyTotals[1].totalC))}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}

                {/* <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"><div className="Indent-2"><b>{t('total_liabilities_equity')}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-(totals[0]+totals[2]+totals[3]))}</b></div></td>
                </tr> */}
                {Object.entries(bsTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].pnl+ccyTotals[1].totalL+ccyTotals[1].totalC != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('total_liabilities_equity')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-(totals[0]+totals[2]+totals[3]))}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-(ccyTotals[1].pnl+ccyTotals[1].totalL+ccyTotals[1].totalC))}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}
            </tbody>
        </HTMLTable>
    </div>

    return (
        <>
            <form className="No-Print" onSubmit={onSubmit}>
                <div className="Row">
                    {/*
                    <FormGroup className="Col-1-1" label={t('from')} labelFor="from">
                        <InputGroup id="from" value={from} type="date" onChange={utils.handleStringChange(s => setFrom(s))} />
                    </FormGroup>
                    */}
                    <FormGroup className="Col-1-1" label={t('as_of')} labelFor="to">
                        <InputGroup id="to" value={to} type="date" onChange={utils.handleStringChange(s => setTo(s))} />
                    </FormGroup>
                    <Button className="Col-2-1 Button-LabeledFormGroup" icon="desktop" type="submit" >{t('preview')}</Button>
                    <AnchorButton className="Col-3-1 Button-LabeledFormGroup" icon="download" target="_blank" href={`${svc.defaults.baseURL}/downloadbs?from=${from}&to=${to}`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>Excel</AnchorButton>
                    {/*<AnchorButton className="Col-4-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadbs?from=${from}&to=${to}&inline`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>PDF</AnchorButton>*/}
                    {/*<AnchorButton className="Col-5-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadbs?from=${from}&to=${to}`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>Download PDF</AnchorButton>*/}
                </div>
                <div className="Row">
                    {/* Checkbox-LabeledFormGroup */}
                    {/*<Checkbox className="Col-1-1" label={t('includes_unposted')} checked={unposted} onChange={utils.handleBooleanChange(v => setUnposted(v))} />*/}
                    <Checkbox className="Col-1-1" label={t('includes_general')} checked={general} onChange={utils.handleBooleanChange(v => setGeneral(v))} />
                    <Checkbox className="Col-2-1" label={t('includes_zero')} checked={zero} onChange={utils.handleBooleanChange(v => setZero(v))} />
                </div>
            </form>
            {list}
        </>
    );
}

export default BalanceSheet